import { useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';

import { getAllFieldsTouched } from '../../../common';
import {
  areAllFieldsTouched,
  getTabStatusFromSnapshot,
  hasFieldValue,
  isEqualTabStatus,
  shouldTouchAllErrorFields,
} from './utils';

import type { AppDispatch } from '../../../../store';
import type { UseQuestionnaireFormikOptions } from './types';
import type { TabStatus } from '../types';

export default function useQuestionnaireFormik<TTabId extends string, TValues extends object>({
  tabId,
  initialValues,
  validationSchema,
  onSubmit,
  extraRequirementMet = true,
  hasNonFormikSavedValues = false,
  updateTabStatus,
}: UseQuestionnaireFormikOptions<TTabId, TValues>) {
  const dispatch = useDispatch<AppDispatch>();
  const previousStatusRef = useRef<TabStatus | null>(null);
  const hasSavedValues = useMemo(
    () => hasFieldValue(initialValues) || hasNonFormikSavedValues,
    [initialValues, hasNonFormikSavedValues],
  );

  const formik = useFormik<TValues>({
    initialValues,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: onSubmit ?? (() => undefined),
  });
  const { dirty, errors, isValid, submitCount, touched, values } = formik;

  useEffect(() => {
    if (
      !shouldTouchAllErrorFields(errors, touched, hasSavedValues) ||
      areAllFieldsTouched(values, touched)
    ) {
      return;
    }

    formik.setTouched(getAllFieldsTouched(values), false);
  }, [errors, formik, hasSavedValues, touched, values]);

  useEffect(() => {
    const nextStatus = getTabStatusFromSnapshot({
      values,
      errors,
      touched,
      dirty,
      hasSavedValues,
      isValid,
      submitCount,
      extraRequirementMet,
    });

    if (previousStatusRef.current != null && isEqualTabStatus(previousStatusRef.current, nextStatus)) {
      return;
    }

    previousStatusRef.current = nextStatus;
    dispatch(updateTabStatus({ tabId, status: nextStatus }));
  }, [
    dirty,
    dispatch,
    errors,
    extraRequirementMet,
    hasSavedValues,
    isValid,
    submitCount,
    tabId,
    touched,
    updateTabStatus,
    values,
  ]);

  return formik;
}
